import { h } from 'preact';
import Store from 'store/store';
import 'store/bolt/gallery';
import { accessNested, sortObjectByOrder, joinClasses } from 'utils/utils';
import { arrayWithUniqueKeys } from 'utils/bolt';
import { getValueForClientSize, isVideosPage } from 'utils/bolt';
import { useDynamicAssetFolder } from 'utils/assets';
import slugify from 'utils/slugify';
import { onMarkdownClick } from 'utils/bolt';
import { getCopy } from 'utils/copy';
import { getPlaylistData } from 'services/boltYoutube';
import { downloadBlob } from 'utils/download';
import { pages } from 'pages/bolt_pages';

import Section from 'components/bolt/section/section';
import Button from 'components/bolt/button/button';
import Download from 'components/bolt/icons/download';
import ArrowDown from 'components/bolt/icons/arrowDown';
import HTML from 'components/core/html/html';
import Link from 'components/bolt/link/link';
import VideoPlayer from 'components/bolt/videos/player';
import SharePopout from 'components/bolt/header/sharePopout';
import Share from 'components/bolt/icons/share';
import Transition from 'components/core/transition/transition';

import s from 'components/bolt/gallery/gallery.sss';

const TOUCH_SLOP = 8;
const TRANSITION_DURATION = 1000;


export default class Gallery extends Section {
	constructor(props) {
		super(props);
		const hasMultipleGalleries = this.hasMultipleGalleries();
		const appData = props.appData;
		const pageData = this.props.pageData || appData?.pages?.[this.props.id]?.data;
		let galleryData = pageData?.contentCarousel;

		if (this.props.isMiniGallery) {
			galleryData = this.props.appData?.options?.paramountFeatures?.toutCarousel?.contentCarousel;
		} else if (this.props.isEmbeded) {
			galleryData = pageData?.gallery?.contentCarousel;
		}

		this.autoRotate = pageData?.autoRotate || false;

		if (props.isMiniGallery) {
			this.autoRotate = props?.appData?.options?.paramountFeatures?.toutCarousel?.autoRotate || false;
		}

		const galleryHash = pageData?.hash;
		const pageName = appData?.pages?.[this.props.id]?.name;
		const pageIdentifier = appData?.copy?.baseCopy?.[pageName] || (pageName?.startsWith('$') ? null : pageName) || props.id;
		const galleryDataArray = sortObjectByOrder(galleryData, (value, key) => ({
			...value,
			fileName: value.fileName || value?.pdfSrc?.split('/')?.pop(),
			key,
			trackKey: slugify(pageIdentifier + '-' + key),
			altKey: galleryHash && slugify(galleryHash + '-' + key),
		}));
		this.unpackYoutubePlaylists(galleryDataArray);

		this.loadedSrcs = [];

		this.state = {
			images: galleryDataArray || [],
			hasMultipleGalleries,
			mounted: false,
		};

		this.observerCallback = this.observerCallback.bind(this);

		this.onDragStart = this.onDragStart.bind(this);
		this.onDrag = this.onDrag.bind(this);
		this.onDragEnd = this.onDragEnd.bind(this);

		this.onImageClick = this.onImageClick.bind(this);
		this.imageLoaded = this.imageLoaded.bind(this);
		this.renderImageLoader = this.renderImageLoader.bind(this);
		this.downloadAll = this.downloadAll.bind(this);
		this.downloadAllAsSingleFile = this.downloadAllAsSingleFile.bind(this);
		this.togglePlaying = this.togglePlaying.bind(this);
		this.videoClickHandler = this.videoClickHandler.bind(this);
		this.onImageShare = this.onImageShare.bind(this);
		this.getImageForHash = this.getImageForHash.bind(this);

		this.useDownloadAttribute = typeof window === 'undefined' || 'download' in document.createElement('a');
	}

	unpackYoutubePlaylists = (array) => {
		const foundYoutubePlaylists = array.find((obj) => obj.youtubePlaylistId);
		if (foundYoutubePlaylists !== undefined) {
			this.replacePlaylistWithVideos(array);
		}
	};

	componentDidMount() {
		super.componentDidMount();
		this.setState({ mounted: true });

		const activeImage = this.getActiveImage();
		this.activeImageUrl = this.getSrc(activeImage);

		if (activeImage) {
			Store.emit('GALLERY_VIEW_IMAGE', activeImage?.trackKey);
		}

		const isVideo =
			activeImage.videoSrc ||
			activeImage.vimeoId ||
			activeImage.youtubeId ||
			activeImage.youtubeId ||
			activeImage.disneySrc ||
			activeImage.youtubePlaylistId;
		if (!isVideo) {
			this.observer = new IntersectionObserver(this.observerCallback, { threshold: [1] });
			this.observer.observe(this.contentWrapper);
		}
	}

	componentWillUnmount() {
		window.clearTimeout(this.nextRotationTimer);
		window.clearTimeout(this.changeImageTimeout);
		if (this.observer) {
			this.observer.unobserve(this.contentWrapper);
			this.observer.disconnect();
		}
	}

	componentDidUpdate(prevProps) {
		if (prevProps.id !== this.props.id) {
			const pageData = this.props.pageData || this.props.appData.pages[this.props.id]?.data;
			let galleryData = pageData?.contentCarousel;

			if (this.props.isMiniGallery) {
				galleryData = accessNested(this.props, 'appData.options.paramountFeatures.toutCarousel.contentCarousel', {});
			} else if (this.props.isEmbeded) {
				galleryData = pageData?.gallery?.contentCarousel;
			}
			const galleryDataArray = sortObjectByOrder(galleryData, (value, key) => ({ ...value, key: slugify(key) }));
			this.unpackYoutubePlaylists(galleryDataArray);
			Store.emit('CHANGE_ACTIVE_IMAGE', {
				image: galleryDataArray[0],
				pageId: this.props.combinedId || this.props.id,
			});

			this.setState({ images: galleryDataArray });
		}
	}

	observerCallback([e]) {
		if (e.isIntersecting && e.intersectionRatio === 1 && !this.firstAutoRotationTriggered) {
			this.firstAutoRotationTriggered = true;
			const activeImage = this.getActiveImage();
			const src = this.getSrc(activeImage);
			this.setAutoRotateTimer(src);
		}
	}

	/**
	 * Returns the gallery data array without playlist objects.
	 *
	 * @param {Array.<object>} array
	 * @returns {Array.<object>} array of objects
	 **/
	removeYoutubePlaylists = (array) => {
		return array.filter((item) => !item.youtubePlaylistId && item);
	};

	replacePlaylistWithVideos(videoDataArray) {
		Promise.all(
			videoDataArray.map((vid) => {
				if (vid && vid.youtubePlaylistId) {
					// return Promise.reject();
					return this.getVideosFromPlaylist(vid);
				}
				return vid;
			})
		)
			.then((data) => {
				const concatArray = [].concat(...data);
				this.setState({ images: arrayWithUniqueKeys(concatArray) });
			})
			.catch(() => {
				this.setState({ images: this.removeYoutubePlaylists(videoDataArray) });
			});
	}

	sortPlaylist(a, b, order) {
		if (!order) {
			return 0;
		}
		const playlistOrder = order.replace(/\s/g, '').split(',').reverse();
		return playlistOrder.indexOf(b.snippet.resourceId.videoId) - playlistOrder.indexOf(a.snippet.resourceId.videoId);
	}

	getVideosFromPlaylist(vid) {
		const playlistId = vid.youtubePlaylistId;
		const noCookies = vid.noCookies === 'No Cookie Mode';
		return getPlaylistData(playlistId).then((data) => {
			return data.items
				.filter((item) => item.snippet.thumbnails.medium)
				.sort((a, b) => this.sortPlaylist(a, b, vid.playlistOrder))
				.map((item) => ({
					name: item.snippet.title,
					thumbSrc: item.snippet.thumbnails.medium.url,
					youtubeId: item.snippet.resourceId.videoId,
					key: item.id,
					noCookies,
				}));
		});
	}

	hasMultipleGalleries() {
		const pages = sortObjectByOrder(accessNested(this.props, 'appData.pages', {}));
		const galleryComps = pages
			.map((page) => {
				if (page.component === 'gallery' || page.page === 'gallery' || page.data?.gallery?.contentCarousel /*embeded gallery*/) {
					return page;
				}
				if (page.type === 'filmPage') {
					const gallerySections = Object.values(page.data || {}).filter((section) => section?.component === 'gallery');
					return gallerySections;
				}
			})
			.filter((page) => page)
			.flat();
		const hasMiniGallery = accessNested(this.props, 'appData.options.paramountFeatures.toutCarousel.contentCarousel', false);
		let totalGalleries = galleryComps.length;
		if (hasMiniGallery) {
			totalGalleries += 1;
		}
		return totalGalleries > 1;
	}

	getActiveImage() {
		const hashImage = this.getImageForHash();
		if (hashImage) return hashImage;

		const { images, hasMultipleGalleries } = this.state;
		const { isMiniGallery } = this.props;
		const store = Store.get();
		const id = isMiniGallery ? 'miniGallery' : this.props.combinedId || this.props.id;
		let activeImageFromStore = hasMultipleGalleries ? accessNested(store, ['gallery', id, 'activeImage']) : accessNested(store, 'gallery.activeImage');
		if (activeImageFromStore) {
			let activeImageInList = images.find((img) => img.key === activeImageFromStore.key);
			if (images.length) {
				activeImageFromStore = activeImageInList || images[0];
			}
		}
		return activeImageFromStore || images[0] || {};
	}

	getImageForHash() {
		const hash = typeof window !== 'undefined' && location.hash.slice(1);
		if (!hash) return;

		const { images } = this.state;

		return images.find((item) => {
			const selectVideoSrc = item.vimeoId || item.youtubeId;
			const isSelectVideo = !!selectVideoSrc;

			if (isSelectVideo) {
				return selectVideoSrc === hash || item.key === hash;
			}

			return item.key === hash || item.altKey === hash;
		});
	}

	changeActiveImage(image) {
		Store.emit('GALLERY_VIEW_IMAGE', image.key);
		const { hasMultipleGalleries } = this.state;
		const { isMiniGallery } = this.props;
		const pageId = hasMultipleGalleries && (isMiniGallery ? 'miniGallery' : this.props.id);

		const isVideo = image.videoSrc || image.vimeoId || image.youtubeId || image.youtubeId || image.disneySrc || image.youtubePlaylistId;

		Store.emit('CHANGE_ACTIVE_IMAGE', { image, pageId: hasMultipleGalleries && (this.props.combinedId || pageId) });
		const newSrc = this.getSrc(image);

		if (this.autoRotate) {
			window.clearTimeout(this.nextRotationTimer);
			!isVideo && this.setAutoRotateTimer(newSrc);
		}

		if (isMiniGallery) {
			return;
		}

		const foundInGallery = this.getImageForHash();
		if (foundInGallery) {
			window.history.replaceState({}, document.title, '.' + location.search);
		}

		const srcHasNotChanged = newSrc === this.activeImageUrl && !isVideo;

		if (!srcHasNotChanged && !this.loadedSrcs.includes(newSrc)) {
			this.setState({ loading: true });
		}

		if (newSrc && !isVideo) {
			this.activeImageUrl = this.getSrc(image);
		}
	}

	getSrc(activeImage) {
		let src;
		if (!activeImage.mobileSrc && !activeImage.tabletSrc) {
			src = activeImage.desktopSrc;
		} else {
			src = {
				desktop: activeImage.desktopSrc,
				mobile: activeImage.mobileSrc,
				tablet: activeImage.tabletSrc,
			};
		}
		return useDynamicAssetFolder(getValueForClientSize(src));
	}

	onDragStart(e) {
		let images = this.state.images || [];
		if (images.length === 1) return;

		const isDesktop = this.props.client?.isDesktopSize;
		const { isMiniGallery } = this.props;
		if (this.grabbed || (e.button !== undefined && e.button !== 0) || isDesktop || isMiniGallery) {
			return;
		}
		if (!e.touches || !e.touches.length) {
			e.preventDefault();
		}
		const target = this.contentWrapper;
		target.style.transition = 'none';
		let eventSource = (e.touches && e.touches[0]) || e;
		let pos = eventSource.pageX || 0;
		this.grabbed = {
			x: pos,
			y: eventSource.pageY || 0,
			previous: pos,
			lastEvent: Date.now(),
		};
	}
	onDrag(e) {
		let images = this.state.images || [];
		if (images.length === 1) return;

		const isDesktop = this.props.client?.isDesktopSize;
		const { isMiniGallery } = this.props;
		if (!this.grabbed || isDesktop || isMiniGallery) {
			return;
		}
		const target = this.contentWrapper;
		let eventSource = (e.touches && e.touches[0]) || e;
		let x = eventSource.pageX || 0;
		let y = eventSource.pageY || 0;
		let deltaX = Math.abs(x - this.grabbed.x);
		let deltaY = Math.abs(y - this.grabbed.y);
		target.style.transform = `translate(${x - this.grabbed.x}px, -50%)`;
		if (!this.grabbed.active && deltaX < TOUCH_SLOP) {
			if (deltaY >= TOUCH_SLOP) {
				// Vertical swipe
				this.grabbed = false;
			}
			return;
		}
		if (!this.grabbed.active) {
			this.grabbed.x = x;
			this.grabbed.active = true;
		}
		if (e.cancelable) {
			e.preventDefault();
		}
		this.grabbed.lastEvent = Date.now();
		this.grabbed.speed = this.grabbed.previous - x;
		this.grabbed.previous = x;
	}
	onDragEnd(e) {
		const isDesktop = this.props.client?.isDesktopSize;
		const { isMiniGallery } = this.props;
		let grabbed = this.grabbed;
		this.grabbed = false;
		if (grabbed) {
			if (!grabbed.active || isDesktop || isMiniGallery) {
				this.onImageClick(e);
				return;
			}
			const target = this.contentWrapper;
			let speed = grabbed.speed * Math.max(0, 1 - (Date.now() - grabbed.lastEvent) / 200);
			if (Math.abs(speed) > 3) {
				this.setState({ swipeToChange: true });
				this.changeImageRelative(speed > 0 ? 1 : -1);
				target.style.transform = `translate(${speed > 0 ? 100 : -100}%, -50%)`;
			}
			this.changeImageTimeout = window.setTimeout(() => {
				target.style.transition = `.3s transform`;
				target.style.transform = `translate(0, -50%)`;
				this.setState({ swipeToChange: false });
			}, 10);
		} else {
			this.contentWrapper.style.transition = `.3s transform`;
			this.contentWrapper.style.transform = `translate(0, -50%)`;
			this.setState({ swipeToChange: false });
		}
	}

	onImageClick() {
		// this.changeImageRelative(e.offsetX >= e.target.width * .5 ? 1 : -1);
	}

	changeImageRelative(offset, opts) {
		// const hash = typeof window !== 'undefined' && location.hash.slice(1);
		let images = this.state.images || [];
		if (images.length === 1) return;

		const activeImage = this.getActiveImage();
		let activeImageIndex = images.findIndex((img) => img.key === activeImage.key);
		if (activeImageIndex === -1) {
			activeImageIndex = 0;
		}
		activeImageIndex = (activeImageIndex + offset) % images.length;
		if (activeImageIndex < 0) activeImageIndex += images.length;

		this.setState({ direction: offset });

		const newImage = images[activeImageIndex];
		if (newImage) {
			this.changeActiveImage(newImage, opts);
		}
	}

	download(image, index, forceDownload) {
		// Avoid downloading youtube videos to avoid breaking the downloadAll fnc
		if (image.youtubeId && !forceDownload) {
			return;
		}

		const localeSlug = this.props.appData?.meta?.title?.localeSlug || 'image';
		const src = image.pdfSrc || this.getSrc(image);

		let filename = image.fileName;
		if (!filename) {
			filename = index ? `${localeSlug}-${index}` : localeSlug;
		}
		return downloadBlob(src, image.fileName || localeSlug);
	}

	downloadAll() {
		this.state.images.forEach((img, key) => this.download(img, key));
		Store.emit('GALLERY_DOWNLOAD_ALL', this.props.id);
	}

	async downloadAllAsSingleFile({ path, fileName }) {
		const isGIF = path.endsWith('.gif');
		const isPDF = path.endsWith('.pdf');
		if (!isGIF && !isPDF) {
			return;
		}

		Store.emit('GALLERY_DOWNLOAD_ALL', this.props.id);
		this.setState({ downloadAllInProgress: true });
		await this.download(path, fileName);
		this.setState({ downloadAllInProgress: false });
	}

	togglePlaying(boolean) {
		this.setState({ playing: boolean });
	}

	triggerImageClick(path) {
		if (this.props.isMiniGallery) {
			Store.emit('TOUT_CLICK', 'carousel', 'tout', path);
		}

		if (path.startsWith('http')) {
			return;
		}
		// Use navigateInternally from utils/bolt ?
		if (path.startsWith('@')) {
			return Store.emit('OPEN_RESERVED_VARIABLE_LINK', path);
		}
		if (isVideosPage(path)) {
			return Store.emit('OPEN_VIDEO_OVERLAY', path);
		}
		if (path === 'boltShowtimes') {
			return Store.emit('SHOWTIMES_SHOW', true);
		}
		if (path === 'boltHomeEnt') {
			return Store.emit('HOMEENT_SHOW', true);
		}
		return Store.emit('CHANGE_ACTIVE_SECTION', path);
	}

	onImageShare(shareMetaOverrides) {
		const activeImage = this.getActiveImage();
		const imageUrl = this.getSrc(activeImage);
		const siteUrl = window.location.href;
		Store.emit('GALLERY_SHARE_IMAGE', {
			imageUrl,
			siteUrl,
			metaOverrides: shareMetaOverrides,
		});
	}

	toggleShare() {
		Store.emit('CLOSE_GALLERY_SHARE');
	}

	setAutoRotateTimer(currentAssetSrc) {
		if (!this.autoRotate || this.autoRotate <= 0) {
			return;
		}

		if (this.loadedSrcs.includes(currentAssetSrc)) {
			this.nextRotationTimer = window.setTimeout(() => {
				this.changeImageRelative(1);
			}, this.autoRotate * 1000);
			return;
		}

		this.autoRotateWaitingForLoad = currentAssetSrc;
	}

	onLoadedHandler = () => {
		this.setState({ loading: false });
	};

	onEndedHandler = (activeVideo) => {
		const nextMediaContent = this.getNextVideo(activeVideo, this.state.images);
		if (nextMediaContent) {
			this.changeImageRelative(1);
		} else {
			Store.emit('VIDEO_ENDED', activeVideo);
		}
	};

	videoClickHandler() {
		if (!this.videoPlayer) {
			return;
		}

		if (!this.state.videoPlaying) {
			this.videoPlayer.play();
		}
	}

	getNextVideo = (activeVideo, videos) => {
		if (!activeVideo || !videos.length) {
			return;
		}
		const activeVidIndex = videos.findIndex((vid) => vid.key === activeVideo.key);
		if (this.autoRotate && activeVidIndex + 1 === videos.length) return videos[0];
		return videos[activeVidIndex + 1];
	};

	renderArrow(direction, offset) {
		const rightArrowAria = accessNested(this.props, 'appData.copy.accessibilityCopy.$RIGHT_ARROW_ARIA');
		const leftArrowAria = accessNested(this.props, 'appData.copy.accessibilityCopy.$LEFT_ARROW_ARIA');

		return (
			<Button
				class={joinClasses(s.arrowBtn, this.state.loading && s.disabled)}
				disabled={this.state.loading}
				trigger={() => this.changeImageRelative(offset)}
				ariaLabel={direction === 'rightArrow' ? rightArrowAria : leftArrowAria}
			>
				<ArrowDown class={s[direction]} />
			</Button>
		);
	}

	renderNavDots(images, activeKey) {
		// const navDotsAria = accessNested(this.props, 'appData.copy.accessibilityCopy.$NAV_DOTS_ARIA');

		return (
			<div class={s.navDots}>
				{images.map((img, index) => {
					const activeDot = activeKey == img.key;
					const isFirst = index === 0;
					const isLast = index === images.length - 1;
					let { direction = 1 } = this.state;
					if (images.length === 2 && activeKey == 0) {
						direction = -1;
					}

					return (
						<button
							class={joinClasses(s.dotBtn, s.pointer)}
							onClick={() => this.changeActiveImage(img)}
							// key={img.key}
							// ariaLabel={navDotsAria.replace('<number>', img.order)}
						>
							<div
								class={joinClasses({
									s,
									dot: true,
									activeDot,
									isFirst,
									isLast,
									goingLeft: this.state.mounted && direction === -1,
									goingRight: this.state.mounted && direction === 1,
								})}
							/>
						</button>
					);
				})}
			</div>
		);
	}

	renderDownloadButtons(activeImage, canDownloadAll, canDownloadSingle, customDownloadAll, fontStyles = {}, isVideo) {
		const copy = accessNested(this.props, 'appData.copy', {});
		if (!canDownloadAll && !canDownloadSingle) {
			return;
		}
		// const isIOS = accessNested(this.props, 'client.isIOS', false);
		// const isSafari = accessNested(this.props, 'client.isSafari', false);
		const isPDF = activeImage.pdfSrc;

		let downloadAllButton;
		if (canDownloadAll) {
			const useCustom = customDownloadAll?.path && customDownloadAll.fileName;
			const isIOS = this.props.client?.isIOS;
			const useDownloadAttribute = useCustom && isIOS;
			const downloadContent = [<span>{copy.baseCopy.$ALL || 'All'}</span>, <Download class={s.downloadIcon} />];
			const inProgress = this.state.downloadAllInProgress;
			const downloadAttrs = {
				class: joinClasses(s.downloadButton, s.downloadAll, isVideo && s.lowerOpacity, inProgress && s.downloadInProgress),
				disabled: isVideo || inProgress,
				key: 'all',
				ariaLabel: copy.accessibilityCopy.$DOWNLOAD_ALL_ARIA,
			};
			if (useDownloadAttribute) {
				downloadAllButton = (
					<a {...downloadAttrs} target="_blank" href={customDownloadAll.path} download={customDownloadAll.fileName}>
						{downloadContent}
					</a>
				);
			} else {
				const trigger = useCustom ? () => this.downloadAllAsSingleFile(customDownloadAll) : this.downloadAll;
				downloadAllButton = (
					<Button {...downloadAttrs} trigger={trigger}>
						{downloadContent}
					</Button>
				);
			}
		}

		return (
			<div class={joinClasses(s.downloadBtnContainer, fontStyles.galleryDownloadButtons)} key="download-buttons">
				{canDownloadSingle && (
					<Button
						class={joinClasses(s.downloadButton, s.downloadSingle, isVideo && s.lowerOpacity)}
						disabled={isVideo}
						trigger={() => {
							this.download(activeImage, activeImage.key);
							Store.emit('GALLERY_DOWNLOAD_IMAGE', activeImage.key);
						}}
						key="single"
						ariaLabel={copy.accessibilityCopy.$DOWNLOAD_IMAGE_ARIA}
					>
						<span>{isPDF ? copy.baseCopy.$PDF || 'PDF' : copy.baseCopy.$IMAGE || 'Image'}</span>
						<Download class={s.downloadIcon} />
					</Button>
				)}
				{downloadAllButton}
			</div>
		);
	}

	renderImageLoader() {
		return <div class={s.spinner} key="loader" />;
	}

	imageLoaded(src) {
		if (!this.loadedSrcs.includes(src)) {
			this.loadedSrcs.push(src);
		}
		this.setState({ loading: false });
		if (this.autoRotateWaitingForLoad === src) {
			this.autoRotateWaitingForLoad = null;
			this.setAutoRotateTimer(src);
		}
	}

	onMarkdownClickCapture() {}

	renderImage() {
		const { loading, swipeToChange } = this.state;
		const activeImage = this.getActiveImage();
		const image = this.getSrc(activeImage);
		const transitionDuration = swipeToChange ? 0 : TRANSITION_DURATION;
		const alt = activeImage.alt && activeImage.alt.charAt(0) === '$' ? getCopy(activeImage.alt) : activeImage.alt || '';

		let elem = (
			<Transition
				duration={transitionDuration}
				class={s.imageTransitionContainer}
				visibleClass={s.imageTransitionVisible}
				leavingClass={s.imageTransitionLeaving}
			>
				<img
					class={joinClasses(s.activeImage, loading && s.loading, activeImage.path && s.clickable)}
					src={image}
					alt={alt}
					ariaLabel={alt}
					onLoad={() => this.imageLoaded(image)}
					ref={($ref) => $ref && (this.$galleryImage = $ref)}
					key={`image-${image}`}
					onMouseDown={this.onDragStart}
					onTouchStart={this.onDragStart}
					onMouseMove={this.onDrag}
					onTouchMove={this.onDrag}
					onMouseUp={this.onDragEnd}
					onTouchEnd={this.onDragEnd}
					onClick={!!activeImage.path && (() => this.triggerImageClick(activeImage.path, activeImage.sameWindow))}
				/>
			</Transition>
		);
		if (activeImage.path?.startsWith('http')) {
			return (
				<Link href={activeImage.path} target={activeImage.sameWindow ? '_self' : '_blank'}>
					{elem}
				</Link>
			);
		}
		return elem;
	}

	renderVideo() {
		const { loading, playing } = this.state;
		const video = this.getActiveImage();
		const isYoutube = video.youtubeId;
		const pageId = this.props?.id;
		const pageData = this.props?.appData?.pages[pageId]?.data || {};

		return (
			<div
				class={joinClasses(s.videoContainer, loading && s.loading)}
				ref={(ref) => (this.videoContainer = ref)}
				onMouseDown={!isYoutube && this.onDragStart}
				onTouchStart={!isYoutube && this.onDragStart}
				onMouseMove={!isYoutube && this.onDrag}
				onTouchMove={!isYoutube && this.onDrag}
				onMouseUp={!isYoutube && this.onDragEnd}
				onTouchEnd={!isYoutube && this.onDragEnd}
			>
				<VideoPlayer
					ref={(ref) => (this.videoPlayer = ref)}
					activeVideo={video}
					onPlay={() => this.togglePlaying(true)}
					onPause={() => this.togglePlaying(false)}
					noAutoplay={video.noAutoplay}
					onLoaded={this.onLoadedHandler}
					onEnded={() => this.onEndedHandler(video)}
					overrideStyles
					muted={pageData.autoMuteVideos}
				/>
				{isYoutube && (
					<div
						onMouseDown={this.onDragStart}
						onTouchStart={this.onDragStart}
						onMouseMove={this.onDrag}
						onTouchMove={this.onDrag}
						onMouseUp={this.onDragEnd}
						onTouchEnd={this.onDragEnd}
						onClick={this.videoClickHandler}
						class={s.touchOverlay}
						style={{ pointerEvents: playing ? 'none' : 'all' }}
					/>
				)}
			</div>
		);
	}

	renderPDF() {
		const pdf = this.getActiveImage();
		const hasThumbnail = pdf.desktopSrc || pdf.mobileSrc || pdf.tabletSrc;

		if (hasThumbnail) {
			return this.renderImage();
		}

		let embed = (
			<embed
				type="application/pdf"
				onClick={pdf.path && (() => this.triggerImageClick(pdf.path, pdf.sameWindow))}
				src={pdf.pdfSrc}
				width="100%"
				height="100%"
				onLoad={() => this.imageLoaded(pdf.pdfSrc)}
				ariaLabel={pdf.ariaLabel}
				onMouseDown={this.onDragStart}
				onTouchStart={this.onDragStart}
				onMouseMove={this.onDrag}
				onTouchMove={this.onDrag}
				onMouseUp={this.onDragEnd}
				onTouchEnd={this.onDragEnd}
			/>
		);

		if (pdf.path?.startsWith('http')) {
			return (
				<Link class={s.activeImage} href={pdf.path} target={pdf.sameWindow ? '_self' : '_blank'}>
					{embed}
				</Link>
			);
		}

		return <div class={s.activeImage}>{embed}</div>;
	}

	renderContent(activeImage, isVideo) {
		let type = isVideo ? 'video' : activeImage.pdfSrc ? 'pdf' : 'image';
		switch (type) {
			case 'video':
				return this.renderVideo();
			case 'pdf':
				return this.renderPDF();
			case 'image':
				return this.renderImage();
		}
	}

	renderShare(isVideo, shareMetaOverrides) {
		const activeImage = this.getActiveImage();
		const imageUrl = this.getSrc(activeImage);
		const baseCopy = this.props?.appData?.copy?.baseCopy || {};
		const accessibilityCopy = this.props?.appData?.copy?.accessibilityCopy || {};
		const shareCopy = baseCopy.$GALLERY_SHARE || baseCopy.$SHARE || 'Share';
		return (
			<div class={s.share}>
				<Button
					class={isVideo && s.lowerOpacity}
					trigger={() => this.onImageShare(shareMetaOverrides)}
					key="image-share"
					ariaLabel={accessibilityCopy.$GALLERY_SHARE_ARIA}
					disabled={isVideo}
				>
					{shareCopy}
					<Share class={s.downloadIcon} aria-hidden />
				</Button>
				<SharePopout
					extraClass={s.sharePopout}
					appData={this.props.appData}
					shareOpen={this.props.store?.gallery?.imageShareLink}
					toggleSharePop={this.toggleShare}
					shareUrl={this.props.store?.gallery?.imageShareLink}
					shareImage={imageUrl}
					key="gallery-share"
				/>
			</div>
		);
	}

	onClick(e) {
		onMarkdownClick(e);
	}

	render(props) {
		let path = `appData.pages.${props.id}.data`;
		if (props.isEmbeded) {
			path = `${path}.gallery`;
		}
		const data = props.pageData || accessNested(props, path) || {};
		const { images, loading, swipeToChange } = this.state;
		const isDesktopSize = accessNested(props, 'client.dimensions.width', 0) >= 1024;
		const activeImage = this.getActiveImage();
		const isVideo =
			activeImage.videoSrc ||
			activeImage.vimeoId ||
			activeImage.youtubeId ||
			activeImage.youtubeId ||
			activeImage.disneySrc ||
			activeImage.youtubePlaylistId;
		const galleryTitle = data.title;
		const canDownloadAll = data.downloadAll;
		const customDownloadAll = data.customDownloadAll || {};
		const canDownloadSingle = data.downloadSingle;
		const canShare = data.share;
		const galleryDescription = data.description;
		const hideArrowsMobile = data.hideArrowsMobile || props.isMiniGallery;
		const hideArrowsDesktop = data.hideArrowsDesktop || props.isMiniGallery;
		const isMobile = accessNested(props, 'client.deviceSize', 'mobile') === 'mobile';
		const shareMetaOverrides = data.shareMetaOverrides;
		const shouldShowControls = images.length > 1;
		const showMobileArrows = shouldShowControls && !isDesktopSize;
		const active = props.store?.header?.activeSection === props.id;
		const fontStyles = accessNested(props, 'appData.styling.fontVariables', {});
		const hasGif = this.state.images.map((image) => this.getSrc(image)).find((src) => src && src.endsWith('gif'));
		const isIOS = accessNested(props, 'client.isIOS', false);
		const isSafari = accessNested(props, 'client.isSafari', false);
		const iosChromeGif = isIOS && !isSafari && hasGif;
		const isSection = accessNested(this.props, ['store', 'appData', 'pages', props.id, 'section']);
		const dedicatedPage = pages[props.id] && !isSection;
		const hasDownloads = (canDownloadAll || canDownloadSingle) && !iosChromeGif;
		const hasExtraActions = hasDownloads || canShare;

		const transitionDuration = swipeToChange ? 0 : TRANSITION_DURATION;

		return (
			<div
				class={joinClasses({
					s,
					container: true,
					active: active,
					hasDownloads: hasDownloads,
					canShare: canShare,
					includeTopArrows: !isDesktopSize && !hideArrowsMobile,
					isEmbeded: props.isEmbeded,
					dedicatedPage,
					miniGallery: props.isMiniGallery,
				})}
				data-section={props.id}
				ref={($ref) => (this.$container = $ref)}
			>
				{this.renderBgContent()}
				<div class={s.inner} key="inner">
					{galleryTitle && <HTML tag="h1" onClickCapture={this.onClick} markdown content={galleryTitle} class={joinClasses(s.galleryTitle, 'h3')} />}
					{galleryDescription && (
						<HTML
							tag="h2"
							onClickCapture={this.onMarkdownClickCapture}
							markdown
							content={galleryDescription}
							class={joinClasses(s.galleryDescription, 'xlarge', isMobile && !hideArrowsMobile && s.withMobileArrows)}
						/>
					)}
					<div class={joinClasses(s.imageWrapper, isVideo && s.videoWrapper)} key="imageWrapper" id={`${props.id}GalleryImageWrapper`}>
						{shouldShowControls && (
							<div
								class={joinClasses(
									s.arrows,
									showMobileArrows && s.mobileArrows,
									!hasDownloads && s.noDownloads,
									hideArrowsMobile && s.hideArrowsMobile,
									hideArrowsDesktop && s.hideArrowsDesktop
								)}
								key="arrows"
							>
								{this.renderArrow('leftArrow', -1)}
								{this.renderArrow('rightArrow', 1)}
							</div>
						)}
						<div class={s.contentWrapper} ref={(ref) => (this.contentWrapper = ref)}>
							{this.renderContent(activeImage, isVideo)}
						</div>
						{loading && this.renderImageLoader()}
					</div>
					<div class={s.bottomWrapper} key="bottomWrapper">
						<div class={s.bottom} key="bottom">
							{shouldShowControls && this.renderNavDots(images, activeImage.key)}
							{activeImage.annotation && (
								<Transition duration={transitionDuration} class={s.annotationTransitionContainer} leavingClass={s.annotationTransitionLeaving}>
									<HTML
										class={joinClasses(s.annotation, fontStyles.galleryAnnotations)}
										content={activeImage.annotation}
										markdown
										key={activeImage.annotation}
									/>
								</Transition>
							)}
							{hasExtraActions && (
								<div class={s.extraActions}>
									{canShare && this.renderShare(isVideo, shareMetaOverrides)}
									{!iosChromeGif &&
										this.renderDownloadButtons(activeImage, canDownloadAll, canDownloadSingle, customDownloadAll, fontStyles, isVideo)}
								</div>
							)}
						</div>
					</div>
				</div>
				{this.renderOverlayedContent()}
			</div>
		);
	}
}
